/**
 * Deterministic PRNG — same seed → same sequence. Browser + Node.
 * Reference: https://en.wikipedia.org/wiki/Xorshift
 */
export function mulberry32(seed: number) {
  let a = seed >>> 0
  return function rng(): number {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export function pick<T>(rng: () => number, arr: readonly T[]): T {
  const idx = Math.floor(rng() * arr.length)
  return arr[Math.min(idx, arr.length - 1)] as T
}

export function pickWeighted<T>(
  rng: () => number,
  items: readonly { value: T; weight: number }[],
): T {
  const total = items.reduce((s, it) => s + it.weight, 0)
  let roll = rng() * total
  for (const it of items) {
    roll -= it.weight
    if (roll <= 0) {
      return it.value
    }
  }
  const fallback = items[items.length - 1]
  if (!fallback) {
    throw new Error("pickWeighted called with empty array")
  }
  return fallback.value
}

export function randInt(rng: () => number, min: number, max: number): number {
  return Math.floor(rng() * (max - min + 1)) + min
}

/** Deterministic guid-ish id using rng. */
export function mockId(rng: () => number, prefix = "id"): string {
  const bytes = Array.from({ length: 12 }, () =>
    Math.floor(rng() * 256)
      .toString(16)
      .padStart(2, "0"),
  ).join("")
  return `${prefix}-${bytes}`
}
